/**
 * 🕸️ XML & XBRL CONTROLLER
 * Location: src/registered/common/parsers/XmlController.js
 * Specialized parser for ESG Linkbases and XML-structured taxonomy reports.
 * Uses fast-xml-parser for high-performance in-memory DOM traversal.
 */
import { XMLParser } from 'fast-xml-parser';

export const XmlController = {
    /**
     * Executes granular discovery on XML/XBRL buffers.
     * @param {ArrayBuffer} buffer - Raw binary data.
     * @param {function} onProgress - Progress reporter for the UI Halo.
     */
    execute: async (buffer, onProgress) => {
        onProgress({ stage: 'OPEN_FILE', status: 'IN_PROGRESS', label: 'Parsing XML structures...' });

        try {
            const text = new TextDecoder().decode(buffer);
            
            // Configuration optimized for ESG XBRL (captures attributes and namespaces)
            const options = {
                ignoreAttributes: false,
                attributeNamePrefix: "@_",
                parseTagValue: true,
                trimValues: true
            };

            const parser = new XMLParser(options);
            const jsonObj = parser.parse(text);

            if (!jsonObj || Object.keys(jsonObj).length === 0) {
                throw new Error("Validation Failure: The XML asset is empty or malformed.");
            }

            onProgress({ stage: 'OPEN_FILE', status: 'SUCCESS', label: 'XML/XBRL DOM initialized.' });

            // STAGE 3: Identify Schema (Success/Fail)
            onProgress({ stage: 'IDENTIFY_SCHEMA', status: 'IN_PROGRESS', label: 'Navigating ESG namespaces...' });

            /**
             * DISCOVERY LOGIC:
             * XBRL files are highly nested. We perform a "Shallow Flatten" to identify 
             * primary data nodes (like <entry>, <item>, or <fact>) to present as tabs.
             */
            const worksheets = [];
            
            // Identify top-level nodes as "virtual worksheets"
            Object.keys(jsonObj).forEach(rootKey => {
                const rootNode = jsonObj[rootKey];
                
                // If the node is an array, it's a perfect candidate for a data sheet
                if (typeof rootNode === 'object') {
                    const headers = Object.keys(Array.isArray(rootNode) ? (rootNode[0] || {}) : rootNode);
                    
                    worksheets.push({
                        name: rootKey.toUpperCase(),
                        headers: headers, // Identify Columns
                        sampleRows: Array.isArray(rootNode) 
                            ? rootNode.slice(0, 10).map(obj => Object.values(obj || {})) 
                            : [Object.values(rootNode)], // Identify Data
                        columnCount: headers.length,
                        rowCount: Array.isArray(rootNode) ? rootNode.length : 1
                    });
                }
            });

            onProgress({ stage: 'IDENTIFY_SCHEMA', status: 'SUCCESS', label: 'ESG Linkbase tags mapped.' });
            
            return { format: 'XML/XBRL', worksheets };

        } catch (err) {
            onProgress({ stage: 'OPEN_FILE', status: 'FAIL', label: 'Invalid XML/XBRL structure.' });
            throw new Error(`XML Parse Error: ${err.message}`);
        }
    }
};